import React, { useState } from 'react';
import {
    Button,
    CardMedia,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Typography
} from '@mui/material';

import whatsNewItemsData from './whatsNewItems.json';
import { useLocalizationContext } from '../../localization/LocalizationContext';
import { orderBy } from 'lodash';
import { makeStyles } from '@mui/styles';
import { useAppConfig } from '../../utils/hooks/user';
import { Theme } from '../../themes/types';
import { getRecentWhatsNewItems } from './whatsNewUtils';

interface WhatsNewItem {
    key: string;
    title: string;
    shortDescription: string;
    description: string;
    image: string; // located in public/assets folder
    date: string;
    appConfigDependencyKey?: string; // in case future under flag
}

type WhatsNewItemsInerface = Record<string, WhatsNewItem[]>;

export const items: WhatsNewItemsInerface = whatsNewItemsData as WhatsNewItemsInerface;

const useStyles = makeStyles<Theme>(theme => ({
    listItem: {
        paddingBottom: '0.5em',
        borderBottom: `1px solid ${theme.palette.contentPaperBorder}`,
        cursor: 'pointer',
        color: theme.palette.modalContentText,

        '&:last-child': {
            borderBottom: 'none'
        },
        '&:hover': {
            backgroundColor: theme.palette.listItemHoverBackground
        }
    },
    media: {
        height: 200,
        objectFit: 'cover',
        width: '100%',
        borderRadius: '0.5em',
        border: `1px solid ${theme.palette.contentPaperBorder}`,
        boxShadow: `rgba(0, 0, 0, 0.2) 0px 2px 1px -1px`,
        marginBottom: '1em'
    },
    chip: {
        fontSize: '0.75rem',
        height: '1.25rem',
        marginRight: '0.1em',
        color: theme.palette.buttonAccentText,
        backgroundColor: theme.palette.buttonAccentBg,
        border: `1px solid ${theme.palette.buttonAccentBg}`
    },
    closeButton: {
        color: theme.palette.buttonAccentBg,
        '&:hover': {
            backgroundColor: theme.palette.buttonAccentSoftBg
        },
        '&:active': {
            backgroundColor: theme.palette.buttonAccentSoftActiveBg
        }
    }
}));

interface WhatsNewItemsProps {
    lang?: keyof typeof items; // e.g. "en" | "de"
    opened: string[];
    onOpen: (key: string) => void;
}

export const WhatsNewItems: React.FC<WhatsNewItemsProps> = ({ lang = 'en', onOpen, opened }) => {
    const localization = useLocalizationContext();
    const classes = useStyles();
    const appConfig = useAppConfig();

    const [selectedItem, setSelectedItem] = useState<WhatsNewItem | null>(null);

    const handleClose = () => {
        setSelectedItem(null);
    };

    const list = orderBy(getRecentWhatsNewItems(items[lang] ?? []), ['date'], ['desc']).filter(
        item => !item.appConfigDependencyKey || appConfig[item.appConfigDependencyKey]
    ) as WhatsNewItem[];

    return (
        <React.Fragment>
            <List
                sx={theme => ({
                    width: '100%',
                    maxWidth: 360,
                    bgcolor: theme.palette.modalListBackground,
                    color: theme.palette.modalContentText,
                    padding: 0
                })}
            >
                {list.map(item => {
                    const isNew = !opened?.includes(item.key);

                    return (
                        <React.Fragment key={item.key}>
                            <ListItem
                                className={classes.listItem}
                                onClick={() => {
                                    setSelectedItem(item);
                                    onOpen(item.key);
                                }}
                            >
                                <ListItemText
                                    secondaryTypographyProps={{ component: 'div' }}
                                    primary={
                                        !isNew ? (
                                            item.title
                                        ) : (
                                            <React.Fragment>
                                                <Chip
                                                    label={localization.getText('whatsNew.new')}
                                                    size="small"
                                                    className={classes.chip}
                                                />
                                                {` `}
                                                <b>{item.title}</b>
                                            </React.Fragment>
                                        )
                                    }
                                    secondary={
                                        <React.Fragment>
                                            <Typography
                                                variant="body1"
                                                sx={theme => ({
                                                    color: theme.palette.modalContentText
                                                })}
                                            >
                                                {item.shortDescription}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={theme => ({
                                                    mt: '4px',
                                                    color: theme.palette.secondaryTextColor
                                                })}
                                            >
                                                {localization.formatDateNumeric(new Date(item.date))}
                                            </Typography>
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                        </React.Fragment>
                    );
                })}
            </List>

            <Dialog
                open={!!selectedItem}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                data-test="whats-new-details-dialog"
                data-test-id="whats-new-details-dialog"
            >
                {selectedItem && (
                    <React.Fragment>
                        <DialogTitle data-test-id="whats-new-details-title">
                            <Typography
                                variant="h6"
                                component="span"
                                color="text.primary"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    width: '100%'
                                }}
                            >
                                <Typography component="span" variant="h6" color="text.primary">
                                    {selectedItem.title}
                                </Typography>
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {localization.formatDateNumeric(new Date(selectedItem.date))}
                                </Typography>
                            </Typography>
                        </DialogTitle>
                        <DialogContent data-test-id="whats-new-details-content">
                            {selectedItem.image && (
                                <CardMedia
                                    component="img"
                                    image={selectedItem.image}
                                    alt={selectedItem.title}
                                    className={classes.media}
                                />
                            )}
                            <Typography
                                variant="body1"
                                sx={theme => ({
                                    color: theme.palette.contentPaperText
                                })}
                            >
                                {selectedItem.description}
                            </Typography>
                        </DialogContent>
                        <DialogActions data-test-id="whats-new-details-actions">
                            <Button onClick={handleClose} className={classes.closeButton}>
                                {localization.getText('whatsNew.close')}
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                )}
            </Dialog>
        </React.Fragment>
    );
};
